<template>
  <form id="create-interview"
        class="interview-create-form"
        @keyup="errors.clear($event.target.name)"
        @change="errors.clear($event.target.name)">
    <input type="hidden" name="image_crop" v-model="interview.image.crop_id">
    <input type="hidden" name="file_id" id="file_id" v-model="interview.image.id">
    <input type="hidden" name="entity_type" id="entity_type" value="interview">
    <input type="hidden" id="mark_as_unexpired" value="1">

    <div v-if="interview.comment && !reviewMode" class="message-box warning-message comment-show">
      <p>{{ interview.comment }}</p>
    </div>

    <div v-if="reviewMode"
         class="set-after comment-field"
         :class="{'has-error': errors && errors.has('comment')}">
      <label for="comment">Your comment (if rejected)</label>

      <span v-if="errors && errors.has('comment')"
            class="help-block">
        <strong>{{ errors.get('comment') }}</strong>
      </span>

      <textarea name="comment"
                id="comment"
                cols="30"
                rows="3"
                class="long-input"
                v-model="interview.comment"></textarea>
    </div>

    <div class="set-after email-field"
         v-if="interview.requiredEmail"
         :class="{'has-error': errors && errors.has('email')}">
      <label v-if="!reviewMode">Your email</label>
      <label v-if="reviewMode">Illustrator's email</label>

      <span v-if="errors && errors.has('email')"
            class="help-block">
        <strong>{{ errors.get('email') }}</strong>
      </span>

      <input type="email"
             name="email"
             class="long-input"
             v-model="interview.email">
    </div>

    <div class="interview-cover set-after"
         :class="{'has-error': errors && errors.has('image.id')}">
      <label>Cover Interview (700x700px minimum)</label>

      <span v-if="errors && errors.has('image.id')"
            class="help-block">
        <strong>{{ errors.get('image.id') }}</strong>
      </span>

      <div class="image-input">
        <input type="file"
               accept="image/png,image/jpeg"
               name="image"
               class="need-crop"
               @change="errors.clear('image.id')"
               id="cover_interview_image">

        <img id="cover_image"
             ref="interviewImage"
             :class="{'hidden': !interview.image.public_path}"
             :src="interview.image.public_path"
             style="width: 100px; height: 100px;"
             data-rjs="2">

        <div v-if="!interview.image.public_path" class="image-cell main-image"></div>

        <button class="upload-image"
                type="button"
                :disabled="disabled"
                data-indicator-input-name="image_crop">Upload Image
        </button>

        <strong></strong>
      </div>
    </div>

    <div v-if="adminMode">
      <div class="interview-name set-after"
           :class="{'has-error': errors && errors.has('name')}">
        <label for="name">Name</label>

        <span v-if="errors && errors.has('name')"
              class="help-block">
          <strong>{{ errors.get('name') }}</strong>
        </span>

        <input type="text"
               id="name"
               class="long-input"
               name="name"
               v-model="interview.name">
      </div>

      <div class="interview-position set-after"
           :class="{'has-error': errors && errors.has('position')}">
        <label for="position">Position</label>

        <span v-if="errors && errors.has('position')"
              class="help-block">
          <strong>{{ errors.get('position') }}</strong>
        </span>

        <input type="text"
               id="position"
               class="long-input"
               name="position"
               v-model="interview.position">
      </div>
    </div>

    <div v-if="!adminMode && ! reviewMode" class="tips">
      <div class="message-box info-message">
        <p>Click on the questions you wish to answer below.</p>
        <i class="fa fa-times alert-box__close"></i>
      </div>

      <div class="message-box warning-message">
        <div class="interview-create__tips-list">
          <p><strong>Tips:</strong></p>
          <p>Remain professional & positive.</p>
          <p>Showing personality is great, but always remember your audience.</p>
          <p>Avoid saying anything you wouldn’t communicate directly to a client.</p>
          <p>Upbeat responses will appeal to potential commissioners.</p>
          <p>Remember one word answers rarely make for good reading, so make it interesting and lively!</p>
        </div>
        <i class="fa fa-times alert-box__close"></i>
      </div>
    </div>

    <div v-if="!adminMode || (adminMode && reviewMode)" class="interview-questions">
      <div class="question" v-for="(question, indexQuestion) in interview.questions">
        <h4 @click="expandQuestion(question)" class="interview-question">
          {{ question.question }}
          <small v-show="question.expanded" class="small-text">expanded</small>
          <small class="interview-validation"></small>
        </h4>

        <div v-show="question.expanded">
          <div class="answer" v-for="(answer, indexAnswer) in question.answers">
            <div class="set-after"
                 v-if="reviewMode"
                 :class="{'has-error': errors.has(`questions.${indexQuestion}.answers.${indexAnswer}.comment`)}">
              <label>Your comment (optional)</label>

              <span v-if="errors.has(`questions.${indexQuestion}.answers.${indexAnswer}.comment`)"
                    class="help-block">
                <strong>{{ errors.get(`questions.${indexQuestion}.answers.${indexAnswer}.comment`) }}</strong>
              </span>

              <textarea cols="30"
                        rows="3"
                        class="long-input"
                        v-model="answer.comment"></textarea>
            </div>

            <div class="message-box warning-message" v-if="answer.comment && !reviewMode">
              <p>{{ answer.comment }}</p>
            </div>

            <div class="interview-answer-text set-after"
                 :class="{'has-error': errors.has(`questions.${indexQuestion}.answers.${indexAnswer}.text`)}">
              <label>{{ indexAnswer ? 'Expanded answer' : 'Answer' }}</label>

              <span v-if="errors.has(`questions.${indexQuestion}.answers.${indexAnswer}.text`)"
                    class="help-block">
                <strong>{{ errors.get(`questions.${indexQuestion}.answers.${indexAnswer}.text`) }}</strong>
              </span>

              <TextEditor
                  :disabled="disabled"
                  @typing="errors.clear(`questions.${indexQuestion}.answers.${indexAnswer}.text`)"
                  v-model="answer.text"/>
            </div>

            <div class="interview-answer-images set-after">
              <label>Related images (optional)</label>

              <InterviewAnswerImages
                  :disabled="disabled"

                  v-model="answer.images"
              />
            </div>

            <div class="set-after">
              <label>Upload Video (optional)</label>
              <UploadVideo
                  url="/ajax/video/upload"
                  :name="answer.video_name"
                  :disabled="disabled"
                  v-model="answer.video_path"
              />
            </div>

            <div class="text-center">
              <button class="transparent-button remove-answer"
                      :disabled="disabled"
                      @click.prevent="removeAnswerFromQuestion(question, answer, $event)">
                Remove Answer
              </button>
            </div>
          </div>

          <div class="text-center expand-answer">
            <button class="big-purple-button"
                    :disabled="disabled"
                    @click.prevent="addAnswerToQuestion(question, $event)">Expand Answer
            </button>
          </div>
        </div>

      </div>
    </div>

    <div v-if="adminMode && !reviewMode" class="interview-questions">
      <div class="question" v-for="(question, indexQuestion) in interview.questions">
        <div class="set-after interview-question-field">
          <label>Question</label>

          <input type="text"
                 class="long-input"
                 v-model="question.question">
        </div>

        <div>
          <div class="answer" v-for="(answer, indexAnswer) in question.answers">
            <div class="interview-answer-text set-after"
                 :class="{'has-error': errors.has(`questions.${indexQuestion}.answers.${indexAnswer}.text`)}">
              <label>{{ indexAnswer ? 'Expanded answer' : 'Answer' }}</label>

              <span v-if="errors.has(`questions.${indexQuestion}.answers.${indexAnswer}.text`)"
                    class="help-block">
                <strong>{{ errors.get(`questions.${indexQuestion}.answers.${indexAnswer}.text`) }}</strong>
              </span>

              <TextEditor
                  :disabled="disabled"
                  @typing="errors.clear(`questions.${indexQuestion}.answers.${indexAnswer}.text`)"
                  v-model="answer.text"/>
            </div>

            <div class="interview-answer-images set-after">
              <label>Related images (optional)</label>

              <InterviewAnswerImages
                  v-model="answer.images"
              />
            </div>

            <div class="set-after">
              <label>Upload Video (optional)</label>
              <UploadVideo
                  url="/ajax/video/upload"
                  :name="answer.video_name"
                  :disabled="disabled"
                  v-model="answer.video_path"
              />
            </div>

            <div class="text-center">
              <button class="transparent-button remove-answer"
                      :disabled="disabled"
                      @click.prevent="removeAnswerFromQuestion(question, answer, $event)">
                Remove Answer
              </button>
            </div>
          </div>
        </div>

        <div class="text-center expand-answer">
          <button class="big-purple-button"
                  :disabled="disabled"
                  @click.prevent="addAnswerToQuestion(question, $event)">Expand Answer
          </button>
        </div>

        <div class="text-center delete-block">
          <button class="transparent-button"
                  :disabled="disabled"
                  @click.prevent="removeQuestion(indexQuestion)">Delete block
          </button>
        </div>
      </div>

      <div class="text-center add-more">
        <button class="big-purple-button"
                :disabled="disabled"
                @click.prevent="addQuestion">Add more
        </button>
      </div>
    </div>

    <div class="buttons-block">
      <div v-if="!adminMode && !reviewMode" class="manage-buttons">
        <button :disabled="disabled"
                @click.prevent="saveDraft"
                class="big-purple-button">Save draft
        </button>

        <button :disabled="disabled"
                @click.prevent="preview"
                class="big-purple-button">Preview
        </button>

        <button :disabled="disabled"
                @click.prevent="submit"
                class="big-purple-button">Submit
        </button>
      </div>

      <div v-if="adminMode && !reviewMode" class="manage-buttons">
        <button :disabled="disabled"
                @click.prevent="saveDraftByAdmin"
                class="big-purple-button">Save
        </button>

        <button :disabled="disabled"
                @click.prevent="preview"
                class="big-purple-button">Preview
        </button>

        <button :disabled="disabled"
                @click.prevent="submitByAdmin"
                class="big-purple-button">Save and Set to Life
        </button>
      </div>

      <div v-if="reviewMode" class="manage-buttons">
        <button @click.prevent="preview"
                class="big-purple-button">Preview
        </button>

        <button @click.prevent="approve"
                class="big-purple-button">Approve
        </button>

        <button @click.prevent="reject"
                class="transparent-button">Reject
        </button>
      </div>

      <button v-if="interview.id"
              @click.prevent="remove"
              class="transparent-button">Delete
      </button>
    </div>
  </form>
</template>

<script>
  import interviewAPI from './../api/interview';

  import Error from './../helpers/errors';

  import InterviewAnswerImages from './../components/InterviewAnswerImages';
  import UploadVideo from './../components/UploadVideo';
  import TextEditor from './TextEditor';
  import modal from './../services/modal';

  import {interviewStatuses, userRoles} from './../constants';

  export default {
    components: {
      TextEditor,
      UploadVideo,
      InterviewAnswerImages,
    },

    props: {
      reviewMode: {
        type: Boolean,
        required: false,
        default() {
          return false;
        }
      },

      adminMode: {
        type: Boolean,
        required: false,
        default() {
          return false;
        }
      },

      interviewId: {
        type: Number,
        required: false,
        default() {
          return null;
        }
      },

      loggedUser: {
        type: Object,
        required: false,
        default() {
          return {};
        }
      },
    },

    data() {
      return {
        interview: {
          image: {
            public_path: '',
            crop_id: '',
            id: ''
          },
        },

        errors: new Error(),
      };
    },

    created() {
      let method = this.reviewMode ? 'review' : 'get';

      interviewAPI
        [method](this.interviewId)
        .then((interview) => {
          this.interview = this.prepareData(interview);

          if (this.adminMode && !this.reviewMode && !this.interview.questions.length) {
            this.addQuestion();
          }
        });
    },

    watch: {
      'interview.image.crop_id': function () {
        this.setCropImage();
      },
    },

    computed: {
      disabled() {
        return this.isDisabled();
      }
    },

    methods: {
      expandQuestion(question) {
        question.expanded = !question.expanded;
      },

      prepareData(interview) {
        let preparedInterview = {
          action: null,
          name: null,
          position: null,
          ...interview,
        };

        if (!preparedInterview.image) {
          preparedInterview.image = {
            public_path: '',
            crop_id: '',
            id: ''
          };
        }

        preparedInterview.questions.forEach((question, index) => {
          question.expanded = !index;

          if (!question.answers.length) {
            question.answers.push({
              text: '',
              images: [],
            });
          } else {
            question.expanded = true;
          }

          question.answers.forEach((answer) => {
            answer.images.forEach((image) => {
              image.imagePath = image.file.public_path;
              image.image = image.file.public_path;
              image.id = image.file_id;

              if (!image.hyperlink) {
                image.hyperlink = {text: ''}
              }
            });
          });
        });

        return preparedInterview;
      },

      setCropImage() {
        if (!this.interview.image.crop_id) return;

        this.interview.image.public_path = this.$refs.interviewImage.src;
      },

      addAnswerToQuestion(question, event) {
        question.answers.push({
          text: '',
          comment: '',
          images: [],
        });

        if (! event) return;

        window.scrollTo({
          top: event.target.offsetTop,
          behavior: "smooth"
        });
      },

      removeAnswerFromQuestion(question, answer, event) {
        modal
          .confirmation({
            text: 'Are you sure you would like to remove the answer?'
          })
          .then((response) => {
            if (!response.value) return;

            question.answers.splice(question.answers.indexOf(answer), 1);

            if (!question.answers.length) {
              this.addAnswerToQuestion(question);

              window.scrollTo({
                top: event.target.parentElement.parentElement.offsetTop - 50,
                behavior: "smooth"
              });
            }

            this.$nextTick(() => {
              this.$children.forEach((child) => {
                child.$emit('refreshEditor');
              });
            });

            this.$nextTick(() => {
              this.$children.forEach((child) => {
                child.$emit('refreshVideo');
              });
            });
          });
      },

      saveDraft(event, needReturnPromise = false) {
        this.interview.action = 'draft';

        if (needReturnPromise) {
          return this.submit();
        }

        this.submit().catch(() => {});
      },

      saveDraftByAdmin() {
        this.saveDraft(null, true)
          .then(() => {
            this.toAdminInterviews();
          })
          .catch(() => {});
      },

      submitByAdmin() {
        this.submit()
          .then(() => {
            this.toAdminInterviews();
          })
          .catch(() => {});
      },

      preview() {
        this.saveDraft(null, true)
          .then(() => {
          this.toPreview();
          })
          .catch(() => {});
      },

      submit() {
        let dataForRequest = this.getDataForRequest();

        this.$root.showMainLoadingBar = true;

        return new Promise((resolve, reject) => {
          interviewAPI.post(dataForRequest)
            .then((interview) => {
              this.$root.showMainLoadingBar = false;

              toastr.success(`Interview saved ${this.interview.action === 'draft' ? '' : 'and submitted for approval'}`);

              this.interview.action = null;
              this.interview.id = interview.id;
              this.interview.status = interview.status;

              this.resetComments();

              resolve();
            })
            .catch((error) => {
              this.errors.record(error.response.data.errors);

              this.$root.showMainLoadingBar = false;

              toastr.error(`Please check/complete the fields highlighted.`);

              window.scrollTo({
                top: 0,
                behavior: "smooth"
              });

              reject(error);
            });
        });
      },

      remove() {
        if (!this.interview.id) return;

        modal
          .confirmation({
            text: 'Are you sure you would like to remove the interview?'
          })
          .then((response) => {
            if (!response.value) return;

            this.$root.showMainLoadingBar = true;

            interviewAPI.delete(this.interview.id)
              .then(() => {
                this.resetInterview();

                this.$root.showMainLoadingBar = false;

                toastr.success('Interview deleted successfully!');

                if (this.adminMode) this.toAdminInterviews();
              });
          });
      },

      approve() {
        let dataForRequest = this.getDataForRequest();

        this.$root.showMainLoadingBar = true;

        interviewAPI.approve(dataForRequest).then(() => {
          this.$root.showMainLoadingBar = false;

          toastr.success('Interview approved successfully');

          this.$nextTick(() => {
            this.adminMode ? this.toAdminInterviews() : this.toAgencyInterviews();
          });
        });
      },

      reject() {
        let dataForRequest = this.getDataForRequest();

        this.$root.showMainLoadingBar = true;

        interviewAPI.reject(dataForRequest).then(() => {
          this.$root.showMainLoadingBar = false;

          toastr.success('Interview rejected successfully');

          this.$nextTick(() => {
            this.adminMode ? this.toAdminInterviews() : this.toAgencyInterviews();
          });
        }).catch((errors) => {
          this.$root.showMainLoadingBar = false;

          this.errors.record(errors.response.data.errors);
        });
      },

      resetInterview() {
        this.interview.id = '';
        this.interview.name = '';
        this.interview.comment = '';
        this.interview.position = '';
        this.interview.image.public_path = '';
        this.interview.image.id = '';
        this.interview.status = interviewStatuses.DRAFT;

        this.interview.questions.forEach((question) => {
          question.answers = [{
            text: '',
            comment: '',
            images: [],
            video_name: '',
            video_path: '',
          }];
        });

        this.$nextTick(() => {
          this.$children.forEach((child) => {
            child.$emit('resetEditor');
          });
        });
      },

      resetComments() {
        this.interview.comment = '';

        this.interview.questions.forEach((question) => {
          question.answers.comment = '';
        });
      },

      isDisabled() {
        if (this.adminMode) return false;

        if (this.reviewMode) {
          return this.interview.status === interviewStatuses.REVIEW_ADMIN &&
            this.loggedUser.role_id === userRoles.AGENCY;
        }

        return this.interview.status === interviewStatuses.REVIEW_AGENCY ||
          this.interview.status === interviewStatuses.REVIEW_ADMIN;
      },

      getDataForRequest() {
        let dataForRequest = this.interview;

        dataForRequest.questions.forEach((question) => {
          question.answers.forEach((answer) => {
            answer.images = answer.images.filter((image) => {
              return !!image.file_id;
            });
          });
        });

        return dataForRequest;
      },

      addQuestion() {
        let question = {
          question: '',
          answers: [],
        };

        question.answers.push({
          text: '',
          comment: '',
          images: [],
        });

        this.interview.questions.push(question);
      },

      removeQuestion(questionIndex) {
        modal
          .confirmation({
            text: 'Are you sure you would like to remove the question?'
          })
          .then((response) => {
            if (!response.value) return;

            this.interview.questions.splice(questionIndex, 1);

            this.$nextTick(() => {
              this.$children.forEach((child) => {
                child.$emit('refreshEditor');
              });
            });

            this.$nextTick(() => {
              this.$children.forEach((child) => {
                child.$emit('refreshVideo');
              });
            });
          });
      },

      toAdminInterviews() {
        location.replace(interviewAPI.adminInterviewsUrl);
      },

      toAgencyInterviews() {
        location.replace(interviewAPI.agencyInterviewsUrl);
      },

      toPreview() {
        location.replace(interviewAPI.previewUrl.replace(':id', this.interview.id));
      },
    },
  }
</script>

<style lang="scss">
  #dashboard-content {
    padding-right: 0 !important;
    padding-left: 0 !important;
  }
</style>

<style lang="scss" scoped>
  #dashboard-content form {
    max-width: unset;
    padding-right: 0;
    padding-left: 0;
  }

  #dashboard-content .interview-create-form .message-box {
    max-width: 800px;
    margin: 0 auto;
  }

  .manage-buttons {
    display: inline-block;
  }

  .interview-create-form {
    max-width: unset;

    .comment-field,
    .comment-show,
    .email-field,
    .interview-cover,
    .interview-name,
    .interview-position,
    .interview-question-field,
    .tips,
    .question h4,
    .answer div,
    .expand-answer,
    .delete-block,
    .add-more,
    .buttons-block {
      max-width: 800px;
      margin: 10px auto;
    }

    .remove-answer {
      margin-bottom: 0;
    }
  }

  .question {
    .answer:nth-child(2n) {
      background-color: lightgrey;
    }

    .answer:not(:first-child) {
      padding-top: 10px;
      padding-bottom: 10px;
    }
  }
</style>
